import type React from "react"
import { useState } from "react"
import { Eye, EyeSlash } from "@phosphor-icons/react"
import { keepPreviousData } from "@tanstack/react-query"
import { toast } from "react-toastify"

import { Button } from "~/components/reusable/Button"
import { ConfirmationDialog } from "~/components/reusable/ConfirmationDialog"
import { hideBlockedByErpMessage, hideNeedsCollectionRemovalMessage } from "~/constants/npi"
import { useSaveMetadata } from "~/hooks/useMetadata"
import { api } from "~/utils/api"

type HideMode = "hide" | "unhide" | "mixed"

type HideImpact = { name: string; erp: boolean; variantIds: string[] }[]

const removalNote = (hakioCollections: HideImpact, hideCount: number): string => {
  if (hakioCollections.length === 0) return ""
  const count = new Set(hakioCollections.flatMap((c) => c.variantIds)).size
  const names = hakioCollections.map((c) => c.name)
  return ` ${hideNeedsCollectionRemovalMessage(names, count, hideCount)}`
}

type Props = {
  variantIds: string[]
  mode?: HideMode
  hiddenCount?: number
  trigger?: React.ReactNode
  disabled?: boolean
  disabledTooltip?: string
  onSuccess?: () => void
}

export function HideVariantsDialog({
  variantIds,
  mode = "hide",
  hiddenCount = 0,
  trigger,
  disabled,
  disabledTooltip,
  onSuccess,
}: Props) {
  const [open, setOpen] = useState(false)
  const saveMetadata = useSaveMetadata({ showToast: false })

  const { data: skuIds } = api.sku.getSkuIdsForVariants.useQuery(
    { variantIds },
    { enabled: variantIds.length > 0 },
  )

  const isUnhide = mode === "unhide"
  const unhiddenCount = variantIds.length - hiddenCount

  const { data: impact = [] } = api.product.hideImpact.useQuery(
    { variantIds },
    { enabled: variantIds.length > 0 && !isUnhide, placeholderData: keepPreviousData },
  )
  const erpCollectionNames = impact.filter((c) => c.erp).map((c) => c.name)
  const hakioCollections = impact.filter((c) => !c.erp)
  const removesFromCollections = !isUnhide && hakioCollections.length > 0
  const blockedTooltip =
    disabledTooltip ??
    (erpCollectionNames.length > 0 ? hideBlockedByErpMessage(erpCollectionNames) : undefined)

  const handleConfirm = () => {
    if (!skuIds || skuIds.length === 0) return

    saveMetadata.mutate(
      { skuIds, ignore: !isUnhide, removeFromCollections: removesFromCollections || undefined },
      {
        onSuccess: () => {
          const message = isUnhide
            ? `${variantIds.length} variant(s) unhidden`
            : `${variantIds.length} variant(s) hidden. You can find them in Product Management → Hidden tab.`
          toast.success(message)
          setOpen(false)
          onSuccess?.()
        },
      },
    )
  }

  const title = isUnhide
    ? `Unhide ${variantIds.length} variant${variantIds.length !== 1 ? "s" : ""}`
    : `Hide ${variantIds.length} variant${variantIds.length !== 1 ? "s" : ""}`

  const description = isUnhide
    ? "These variants will become visible on the platform again."
    : mode === "mixed"
      ? `${unhiddenCount} of ${variantIds.length} selected variant(s) will be hidden. ${hiddenCount} variant(s) are already hidden and will not be affected.${removalNote(hakioCollections, unhiddenCount)}`
      : `Hidden variants will no longer appear on the platform.${removalNote(hakioCollections, variantIds.length)} They can always be found and unhidden in Product Management → Hidden tab.`

  const confirmText = saveMetadata.isPending
    ? isUnhide
      ? "Unhiding..."
      : "Hiding..."
    : isUnhide
      ? "Unhide variants"
      : removesFromCollections
        ? "Remove and hide"
        : "Hide variants"

  const Icon = isUnhide ? Eye : EyeSlash
  const label = isUnhide ? "Unhide" : "Hide"

  return (
    <ConfirmationDialog
      open={open}
      onOpenChange={(next) => {
        if (!next && saveMetadata.isPending) return
        setOpen(next)
      }}
      title={title}
      description={description}
      confirmText={confirmText}
      onConfirm={handleConfirm}
      loading={saveMetadata.isPending}
      requires="edit"
      disabled={disabled || erpCollectionNames.length > 0}
      disabledTooltip={blockedTooltip}
      trigger={
        // No `disabled` here: the gated trigger owns it, and Radix slot merging would let a
        // child's own `disabled` shadow the one the gate injects.
        trigger ?? (
          <Button size="sm" variant="neutral">
            <Icon size={16} />
            {label}
          </Button>
        )
      }
    />
  )
}
